/* Shared, deferred product motion. Static posters remain the reliable default. */
(() => {
  if (window.__mvTurntable) return;
  window.__mvTurntable = true;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const fine = matchMedia('(any-pointer: fine)');
  const connection = navigator.connection;
  const cards = new Map();
  let active = null;
  let intent = null;
  const allowed = () => !document.hidden && !reduced.matches && !connection?.saveData;
  const eligible = card => { const dialog = document.querySelector('dialog[open]'); return !dialog || dialog.contains(card); };
  const cardOf = node => node?.closest?.('.mv-card--video');
  function stop(video) {
    if (!video) return;
    video.pause();
    if (active === video) active = null;
  }
  function showFrame(video) {
    if (allowed() && video.readyState >= 2 && !video.error) video.closest('.mv-card__stage')?.classList.add('is-live');
  }
  function start(card) {
    const video = card?.querySelector('.mv-card__video');
    if (!video || !allowed()) return;
    if (active && active !== video) stop(active);
    active = video;
    if (!video.dataset.mvArmed) {
      video.dataset.mvArmed = 'true';
      video.muted = true;
      video.addEventListener('error', () => {
        video.closest('.mv-card__stage')?.classList.remove('is-live');
        stop(video);
      });
      if (!video.requestVideoFrameCallback) video.addEventListener('timeupdate', () => { if (video.currentTime > 0) showFrame(video); });
      video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
      video.preload = 'auto';
      video.load();
    }
    if (video.paused) {
      if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(() => showFrame(video));
      const playback = video.play();
      playback?.catch(() => { if (active === video) active = null; });
    }
  }
  function reconcile() {
    if (!allowed()) { stop(active); return; }
    if (fine.matches) {
      if (intent?.isConnected && eligible(intent) && (cards.get(intent) ?? 0) > 0) start(intent);
      else stop(active);
      return;
    }
    let best = null;
    let visibility = 0.55;
    cards.forEach((ratio, card) => {
      if (ratio >= visibility && card.isConnected && eligible(card)) { best = card; visibility = ratio; }
    });
    if (best) start(best); else stop(active);
  }
  // Observe the media stage, not the full text-heavy card: tall cards on small
  // screens must still be able to reach the playback threshold.
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => cards.set(entry.target.closest('.mv-card'), entry.intersectionRatio));
    reconcile();
  }, { threshold: [0, 0.25, 0.55, 0.75, 1] }) : null;
  function register(root) {
    const nodes = root.matches?.('.mv-card--video') ? [root] : root.querySelectorAll?.('.mv-card--video') || [];
    nodes.forEach(card => {
      if (cards.has(card)) return;
      cards.set(card, observer ? 0 : 1);
      const stage = card.querySelector('.mv-card__stage');
      if (stage) observer?.observe(stage);
    });
  }
  document.addEventListener('pointerover', event => {
    if (!fine.matches) return;
    const card = cardOf(event.target);
    if (card && !card.contains(event.relatedTarget)) { intent = card; reconcile(); }
  });
  document.addEventListener('pointerout', event => {
    const card = cardOf(event.target);
    if (card && !card.contains(event.relatedTarget) && !card.contains(document.activeElement)) {
      intent = null; reconcile();
    }
  });
  document.addEventListener('focusin', event => { intent = cardOf(event.target); reconcile(); });
  document.addEventListener('focusout', event => {
    if (intent && !intent.contains(event.relatedTarget)) { intent = null; reconcile(); }
  });
  document.addEventListener('visibilitychange', reconcile);
  window.addEventListener('pagehide', () => stop(active));
  window.addEventListener('pageshow', reconcile);
  reduced.addEventListener('change', () => {
    if (reduced.matches) cards.forEach((_, card) => card.querySelector('.mv-card__stage')?.classList.remove('is-live'));
    reconcile();
  });
  fine.addEventListener('change', reconcile);
  connection?.addEventListener?.('change', reconcile);
  register(document);
  const mutations = new MutationObserver(records => {
    let changed = false;
    records.forEach(record => {
      record.addedNodes.forEach(node => { if (node.nodeType === 1) { register(node); changed = true; } });
      if (record.removedNodes.length) changed = true;
    });
    if (!changed) return;
    cards.forEach((_, card) => {
      if (!card.isConnected) {
        stop(card.querySelector('video'));
        observer?.unobserve(card.querySelector('.mv-card__stage'));
        cards.delete(card);
      }
    });
    if (active && !active.isConnected) stop(active);
  });
  mutations.observe(document.body, { childList: true, subtree: true });
})();
